import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { catchError, combineLatest, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import * as EnrichedWatchlistActions from './enriched-watchlist.actions';
import { EnrichedWatchlistFacade } from './enriched-watchlist.facade';
import { CategoryMapping, ItemCategory } from './enriched-watchlist.models';
import * as EnrichedWatchlistSelectors from './enriched-watchlist.selectors';

import { WatchlistService } from '../../service/watchlist/watchlist.service';
import * as WatchlistActions from '../watchlist/watchlist.actions';
import { WatchlistFacade } from '../watchlist/watchlist.facade';
import { WatchlistItem } from '../watchlist/watchlist.models';
import { selectWatchlistItemId } from '../watchlist/watchlist.reducer';

@Injectable()
export class EnrichedWatchlistEffects {
  openPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EnrichedWatchlistActions.openPage),
        tap(({ page, itemCategory }) =>
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { page: page > 1 ? page : null, category: itemCategory ?? undefined },
            queryParamsHandling: 'merge',
          })
        )
      ),
    { dispatch: false }
  );

  setItemCategory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EnrichedWatchlistActions.setItemCategory),
        tap(({ itemCategory }) =>
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              category: itemCategory !== ItemCategory.all ? itemCategory : null,
              page: null,
            },
            queryParamsHandling: 'merge',
          })
        )
      ),
    { dispatch: false }
  );

  init$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(EnrichedWatchlistActions.init)),
      this.actions$.pipe(ofType(WatchlistActions.loadWatchlistSuccess, WatchlistActions.addWatchlistItemSuccess)),
    ]).pipe(
      withLatestFrom(this.watchlistFacade.allWatchlistItems$),
      map(([[{ itemCategory }], allWatchlistItems]) =>
        EnrichedWatchlistActions.loadItems({
          items: allWatchlistItems.filter((item) =>
            CategoryMapping.find((mapping) => mapping.category === itemCategory)?.entityTypes.includes(item.entity_type)
          ),
        })
      )
    )
  );

  loadItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnrichedWatchlistActions.loadItems),
      withLatestFrom(this.enrichedFacade.pageSize$, this.store.pipe(select(EnrichedWatchlistSelectors.getCurrentPage))),
      map(([{ items }, pageSize, currentPage]) => {
        const pageCount = Math.ceil(items.length / pageSize);
        if (pageCount <= currentPage) currentPage = pageCount - 1;
        const sliceMax = items.length;
        const sliceStart = currentPage * pageSize;
        let sliceEnd = sliceStart + pageSize;
        sliceEnd = sliceEnd <= sliceMax ? sliceEnd : sliceMax;
        const itemsOnPage = items.slice(sliceStart, sliceEnd);
        return [itemsOnPage, pageCount] as [WatchlistItem[], number];
      }),
      tap(([_, maxPage]) => this.store.dispatch(EnrichedWatchlistActions.setPageCount({ pageCount: maxPage }))),
      withLatestFrom(this.store.pipe(select(EnrichedWatchlistSelectors.getCachedItems))),
      switchMap(([[itemsOnPage], cachedItems]) =>
        forkJoin(
          itemsOnPage.map((rawItem) => {
            const enrichmentData = cachedItems.find(
              (cachedItem) => selectWatchlistItemId(cachedItem) === selectWatchlistItemId(rawItem)
            );
            return enrichmentData ? of(enrichmentData) : this.watchlistService.getEnrichedWatchlistItem(rawItem);
          })
        ).pipe(
          map((enrichedItems) => EnrichedWatchlistActions.loadSuccess({ enrichedItems })),
          catchError((error) => of(EnrichedWatchlistActions.loadError(error)))
        )
      )
    )
  );

  removeItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WatchlistActions.removeWatchlistItemSuccess),
      withLatestFrom(
        this.enrichedFacade.currentPage$,
        this.enrichedFacade.itemCategory$,
        this.enrichedFacade.allItemCategories$.pipe(
          withLatestFrom(this.enrichedFacade.itemCategory$),
          map(
            ([allItemCategories, itemCategory]) =>
              allItemCategories.find((_itemCategory) => _itemCategory.category === itemCategory)?.count
          )
        )
      ),
      map(([_, page, itemCategory, count]) => {
        return count && count > 0
          ? EnrichedWatchlistActions.init({ page, itemCategory })
          : EnrichedWatchlistActions.openPage({ page: 1, itemCategory: ItemCategory.all });
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly watchlistService: WatchlistService,
    private readonly watchlistFacade: WatchlistFacade,
    private readonly enrichedFacade: EnrichedWatchlistFacade,
    private readonly store: Store,
    private router: Router,
    private route: ActivatedRoute
  ) {}
}
